import React, { FC, useMemo } from 'react';
import {
  HStack,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack
} from '@chakra-ui/react';
import {
  useAddress,
  useChain,
  useContract,
  useSwitchChain
} from '@thirdweb-dev/react';
import {
  computePoolAddress,
  Field,
  INITIAL_POOL_FEE,
  Position
} from '@cryptoalgebra/kim-sdk';
import { algebraPositionManagerABI } from '../../../../../abis/v3';
import { useAsyncFn } from 'react-use';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import { IDerivedMintInfo, useDerivedMintInfo } from '@app/state/mintStore';
import { useUserState } from '@app/state/userStore';
import EnterAmounts from '@app/screens/liquidity/AddLiquidityV3/components/EnterAmounts';
import { useCurrency } from '@app/hooks/v3/useCurrency';
import { ethers } from 'ethers';
import { SubmitButton } from '@app/components/SubmitButton';
import { useApprove } from '@app/hooks/v3/useApprove';
import { ApprovalState } from '@app/types/approve-state';
import { getActiveChain } from '@app/constants/chains';
import { useConfig } from '@app/config';
import { useNetworkMismatch } from '@app/hooks/thirdweb/useNetworkMismatch';

interface Props {
  onClose: (txHash?: string) => void;
  isOpen: boolean;
  mintInfo: IDerivedMintInfo;
  position: Position;
  positionId: number;
}

export const AddLiquidityModalV3Content: FC<Props> = ({
  isOpen,
  onClose,
  position,
  positionId
}) => {
  const account = useAddress();
  const chain = useChain();
  const switchChain = useSwitchChain();
  const { txDeadline } = useUserState();
  const currencyA = useCurrency(position.pool.token0.address.toLowerCase());
  const currencyB = useCurrency(position.pool.token1.address.toLowerCase());

  const poolAddress = computePoolAddress({
    tokenA: position.pool.token0,
    tokenB: position.pool.token1
  });

  const mintInfo = useDerivedMintInfo(
    currencyA ?? undefined,
    currencyB ?? undefined,
    poolAddress ?? undefined,
    INITIAL_POOL_FEE,
    currencyA ?? undefined,
    position
  );

  const config = useConfig();
  const isNetworkMismatch = useNetworkMismatch();

  const { contract } = useContract(
    config?.ALGEBRA_ADDRESSES.ALGEBRA_POSITION_MANAGER,
    algebraPositionManagerABI
  );

  const { approvalState: approvalStateA, approvalCallback: approvalCallbackA } =
    useApprove(
      mintInfo.parsedAmounts[Field.CURRENCY_A],
      config?.ALGEBRA_ADDRESSES.ALGEBRA_POSITION_MANAGER
    );
  const { approvalState: approvalStateB, approvalCallback: approvalCallbackB } =
    useApprove(
      mintInfo.parsedAmounts[Field.CURRENCY_B],
      config?.ALGEBRA_ADDRESSES.ALGEBRA_POSITION_MANAGER
    );

  const isReady = useMemo(() => {
    return Boolean(
      (mintInfo.depositADisabled
        ? true
        : approvalStateA === ApprovalState.APPROVED) &&
        (mintInfo.depositBDisabled
          ? true
          : approvalStateB === ApprovalState.APPROVED) &&
        !mintInfo.errorMessage &&
        !mintInfo.invalidRange
    );
  }, [mintInfo, approvalStateA, approvalStateB]);

  const [{ loading: isLoading }, add] = useAsyncFn(async () => {
    if (!positionId || !mintInfo || !contract) {
      return;
    }

    try {
      const res = await contract.call(
        'increaseLiquidity',
        [
          [
            positionId.toString(),
            mintInfo.parsedAmounts.CURRENCY_A
              ? ethers.utils.parseUnits(
                  mintInfo.parsedAmounts.CURRENCY_A!.toSignificant(),
                  mintInfo.parsedAmounts.CURRENCY_A.currency.decimals
                )
              : 0,
            mintInfo.parsedAmounts.CURRENCY_B
              ? ethers.utils.parseUnits(
                  mintInfo.parsedAmounts.CURRENCY_B!.toSignificant(),
                  mintInfo.parsedAmounts.CURRENCY_B.currency.decimals
                )
              : 0,
            0,
            0,
            Date.now() + txDeadline
          ]
        ],
        {
          value: BigInt(0)
        }
      );

      if (res?.reason) {
        showErrorToast('Transaction rejected');
      }

      const txHash = res?.receipt?.transactionHash;

      if (txHash) {
        showSuccessToast(
          `Transaction successful.`,
          <ExternalLink txHash={txHash} />
        );
        onClose(txHash);
      }
    } catch (e: any) {
      console.error(e.message);

      if (e.message?.includes('user rejected transaction')) {
        return;
      }

      showErrorToast(`Transaction failed: ${e.message}`);
    }
  }, [contract, positionId, mintInfo, txDeadline]);

  function renderSubmit() {
    if (isNetworkMismatch) {
      return (
        <SubmitButton
          label="Switch chain"
          isLoading={false}
          variant={'destructive'}
          onClick={() => switchChain(getActiveChain().chainId)}
        />
      );
    }

    if (
      approvalStateA === ApprovalState.NOT_APPROVED ||
      approvalStateA === ApprovalState.PENDING ||
      approvalStateB === ApprovalState.NOT_APPROVED ||
      approvalStateB === ApprovalState.PENDING
    ) {
      return (
        <HStack w="100%" justifyContent="space-between">
          {(approvalStateA === ApprovalState.NOT_APPROVED ||
            approvalStateA === ApprovalState.PENDING) && (
            <SubmitButton
              label={
                approvalStateA === ApprovalState.PENDING
                  ? 'Loading...'
                  : `Approve ${mintInfo.currencies.CURRENCY_A?.symbol}`
              }
              isLoading={approvalStateA === ApprovalState.PENDING}
              disabled={approvalStateA === ApprovalState.PENDING}
              onClick={() => approvalCallbackA && approvalCallbackA()}
            />
          )}
          {(approvalStateB === ApprovalState.NOT_APPROVED ||
            approvalStateB === ApprovalState.PENDING) && (
            <SubmitButton
              isLoading={approvalStateB === ApprovalState.PENDING}
              onClick={() => approvalCallbackB && approvalCallbackB()}
              label={
                approvalStateB === ApprovalState.PENDING
                  ? 'Loading...'
                  : `Approve ${mintInfo.currencies.CURRENCY_B?.symbol}`
              }
            />
          )}
        </HStack>
      );
    }

    if (mintInfo.errorMessage) {
      return (
        <SubmitButton
          label={mintInfo.errorMessage}
          disabled
          isLoading={false}
        />
      );
    }

    return (
      <SubmitButton
        label="Add liquidity to position"
        isLoading={isLoading}
        isDisabled={!position || !mintInfo || isLoading || !isReady}
        onClick={() => add && add()}
      />
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      motionPreset="slideInBottom"
      size="xs"
    >
      <ModalOverlay />
      <ModalContent sx={{ maxWidth: ['100vw', '600px'] }}>
        <ModalHeader>
          <HStack justify="space-between" px={0} py={0} mb="16px">
            <Text fontSize="16px" color="white" fontWeight="500">
              Add liquidity to position
            </Text>
            <ModalCloseButton onClick={() => onClose()} />
          </HStack>
        </ModalHeader>
        <VStack w="100%">
          <EnterAmounts
            currencyA={currencyA}
            currencyB={currencyB}
            mintInfo={mintInfo}
          />
          {renderSubmit()}
        </VStack>
      </ModalContent>
    </Modal>
  );
};
